import { useCallback, useState } from 'react'
import InputField from '../../../components/InputField'
import ApiRoute from '../../../components/ApiRoute'
import Button from '../../../components/Button'

const Medications = ({ activeSession, callApiFn, isApiCalling }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [selectedMed, setSelectedMed] = useState(null)
  const [daysSupply, setDaysSupply] = useState('30')
  const [quantity, setQuantity] = useState('')
  const [stagedMeds, setStagedMeds] = useState([])
  const [removedNdcs, setRemovedNdcs] = useState([])

  const hasContact = Boolean(activeSession?.contact)
  const hasLocalChanges = stagedMeds.length > 0 || removedNdcs.length > 0

  const search = useCallback(async () => {
    if (!searchTerm.trim()) return
    setSearchResults(null)
    const { body } = await callApiFn({
      method: 'GET',
      route: `medications/${searchTerm}`
    })
    setSearchResults(body || [])
    console.log('Medication search results:', JSON.stringify(body?.[0], null, 2))
  }, [searchTerm, callApiFn])

  const fetchContactMedications = useCallback(async () => {
    await callApiFn({
      method: 'GET',
      route: 'contact/{id}/medications',
      saveSuccessPath: 'medications'
    })
  }, [callApiFn])

  const getNdc = (med) => med.ndc || med.NDC || med.Ndc || ''
  const getGenericDesc = (med) => med.genericDescription || med.GenericDescription || ''
  const getDrugName = (med) => med.drugName || med.DrugName || ''

  const selectMed = (med) => {
    setSelectedMed(med)
    setDaysSupply('30')
    setQuantity('')
  }

  const addToStaged = () => {
    if (!selectedMed || !daysSupply || !quantity) return
    const newMed = {
      ndc: getNdc(selectedMed),
      daysSupply: parseInt(daysSupply, 10),
      quantity: parseInt(quantity, 10),
      _label: `${getGenericDesc(selectedMed)} - ${getDrugName(selectedMed)}`
    }
    setStagedMeds([...stagedMeds, newMed])
    setSelectedMed(null)
    setDaysSupply('30')
    setQuantity('')
  }

  const removeFromStaged = (ndc) => {
    setStagedMeds(stagedMeds.filter(m => m.ndc !== ndc))
  }

  const removeFromCurrent = (ndc) => {
    setRemovedNdcs([...removedNdcs, ndc])
  }

  const undoRemove = (ndc) => {
    setRemovedNdcs(removedNdcs.filter(n => n !== ndc))
  }

  const putMedications = useCallback(async () => {
    const remaining = (activeSession?.medications || []).filter(m => !removedNdcs.includes(m.ndc))
    const payload = [
      ...remaining.map(m => ({ ndc: m.ndc, daysSupply: m.daysSupply, quantity: m.quantity })),
      ...stagedMeds.map(m => ({ ndc: m.ndc, daysSupply: m.daysSupply, quantity: m.quantity }))
    ]
    await callApiFn({
      method: 'PUT',
      route: 'contact/{id}/medications',
      body: payload,
      saveSuccessPath: 'medications'
    })
    setStagedMeds([])
    setRemovedNdcs([])
  }, [activeSession, stagedMeds, removedNdcs, callApiFn])

  return (
    <div>
      <ApiRoute method='GET' route='/contact/{contactId}/medications' onClick={fetchContactMedications} disabled={isApiCalling || !hasContact} />

      {!hasContact && (
        <div className='bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded my-2'>
          No contact selected. Create a contact first.
        </div>
      )}

      {/* Current contact medications */}
      {hasContact && (activeSession?.medications || []).length > 0 && (
        <div className='mb-3'>
          <h4 className='font-semibold mb-1'>Current Contact Medications</h4>
          <table className='w-full text-sm border border-gray-300'>
            <thead className='bg-gray-100'>
              <tr>
                <th className='text-left p-1 border-b'>NDC</th>
                <th className='text-left p-1 border-b'>Name</th>
                <th className='text-left p-1 border-b'>Days</th>
                <th className='text-left p-1 border-b'>Qty</th>
                <th className='p-1 border-b' />
              </tr>
            </thead>
            <tbody>
              {(activeSession?.medications || []).map((med) => {
                const isRemoved = removedNdcs.includes(med.ndc)
                return (
                  <tr key={med.ndc} className={`border-b ${isRemoved ? 'bg-red-50 line-through text-gray-400' : ''}`}>
                    <td className='p-1'>{med.ndc}</td>
                    <td className='p-1'>{med.medication?.genericDescription || '-'}</td>
                    <td className='p-1'>{med.daysSupply}</td>
                    <td className='p-1'>{med.quantity}</td>
                    <td className='p-1'>
                      {isRemoved
                        ? <button className='text-blue-600 text-xs hover:underline' onClick={() => undoRemove(med.ndc)}>Undo</button>
                        : <button className='text-red-600 text-xs hover:underline' onClick={() => removeFromCurrent(med.ndc)}>Remove</button>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Save changes button - shown when there are local changes */}
      {hasLocalChanges && (
        <div className='mb-3 border-2 border-orange-400 rounded p-2 bg-orange-50'>
          <h4 className='font-semibold mb-1'>Unsaved Changes</h4>
          {removedNdcs.length > 0 && <div className='text-sm text-red-600 mb-1'>{removedNdcs.length} medication(s) marked for removal</div>}
          {stagedMeds.length > 0 && <div className='text-sm text-green-600 mb-1'>{stagedMeds.length} medication(s) staged to add</div>}
        </div>
      )}

      <ApiRoute method='PUT' route='/contact/{contactId}/medications' onClick={putMedications} disabled={isApiCalling || !hasContact} />

      {/* Staged medications (local state, not yet PUT) */}
      {stagedMeds.length > 0 && (
        <div className='mb-3 border-2 border-blue-400 rounded p-2 bg-blue-50'>
          <h4 className='font-semibold mb-1'>Staged Medications (not yet saved)</h4>
          <table className='w-full text-sm'>
            <thead className='bg-blue-100'>
              <tr>
                <th className='text-left p-1'>NDC</th>
                <th className='text-left p-1'>Name</th>
                <th className='text-left p-1'>Days</th>
                <th className='text-left p-1'>Qty</th>
                <th className='p-1' />
              </tr>
            </thead>
            <tbody>
              {stagedMeds.map((med) => (
                <tr key={med.ndc} className='border-b'>
                  <td className='p-1'>{med.ndc}</td>
                  <td className='p-1'>{med._label}</td>
                  <td className='p-1'>{med.daysSupply}</td>
                  <td className='p-1'>{med.quantity}</td>
                  <td className='p-1'>
                    <button className='text-red-600 text-xs hover:underline' onClick={() => removeFromStaged(med.ndc)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dosage form for selected medication */}
      {selectedMed && (
        <div className='border-2 border-green-400 rounded p-3 mb-3 bg-green-50'>
          <h4 className='font-semibold mb-1'>Selected: {getGenericDesc(selectedMed)}</h4>
          <div className='text-xs text-gray-600 mb-2'>{getDrugName(selectedMed)}</div>
          <div className='flex gap-2 items-end'>
            <InputField labelFor='daysSupply' type='number' id='daysSupply' name='daysSupply' onChange={(e) => setDaysSupply(e.target.value)} value={daysSupply} min='1'>Days Supply</InputField>
            <InputField labelFor='qty' type='number' id='qty' name='qty' onChange={(e) => setQuantity(e.target.value)} value={quantity} min='1'>Quantity</InputField>
            <Button onClick={addToStaged} disabled={!daysSupply || !quantity}>Add</Button>
            <Button onClick={() => setSelectedMed(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Search */}
      <h4 className='font-semibold mt-4 mb-1'>Search Medications</h4>
      <div className='flex gap-2 items-end mb-2'>
        <InputField labelFor='medSearch' type='text' id='medSearch' name='medSearch' onChange={(e) => setSearchTerm(e.target.value)} value={searchTerm} containerClassName='flex-1'>Search Term</InputField>
        <ApiRoute method='GET' route='/medications/{term}' onClick={search} disabled={isApiCalling || !searchTerm.trim()} />
      </div>

      {/* Search results table */}
      {searchResults !== null && searchResults.length === 0 && (
        <div className='text-gray-500 italic'>No medications found for "{searchTerm}"</div>
      )}

      {searchResults !== null && searchResults.length > 0 && (
        <div>
          <div className='text-sm text-gray-600 mb-1'>Found {searchResults.length} result(s)</div>
          <table className='w-full text-sm border border-gray-300'>
            <thead className='bg-gray-100'>
              <tr>
                <th className='text-left p-1 border-b'>NDC</th>
                <th className='text-left p-1 border-b'>Generic Description</th>
                <th className='text-left p-1 border-b'>Drug Name</th>
                <th className='p-1 border-b' />
              </tr>
            </thead>
            <tbody>
              {searchResults.map((med) => (
                <tr key={getNdc(med)} className='border-b hover:bg-gray-50'>
                  <td className='p-1'>{getNdc(med)}</td>
                  <td className='p-1'>{getGenericDesc(med)}</td>
                  <td className='p-1 text-xs'>{getDrugName(med)}</td>
                  <td className='p-1'>
                    <button
                      className='text-blue-600 text-xs hover:underline disabled:text-gray-400'
                      onClick={() => selectMed(med)}
                      disabled={!hasContact || isApiCalling}
                    >
                      Select
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default Medications
